import { GameObject } from '../base/GameObject';
import { getEquipmentManager } from '../base/UserProfile';
import { EquipmentStats } from '../../data/EquipmentData';
import { AudioManager, SoundEffect } from '../../systems/AudioManager';

export abstract class Earth extends GameObject {
  protected baseMaxHealth: number;
  protected baseDefense: number; // Damage resistance (0-1)
  protected baseEnergyRegen: number; // Energy per second
  protected towerLimit: number;
  protected cooldown: number; // Ability cooldown in ms
  protected lastAbilityTime: number = 0;
  protected audioManager: AudioManager;
  protected level: number = 1;
  
  // Properties that subclasses expect
  protected damageResistance: number;
  protected specialAbility: string;
  public abilityCooldown: number;

  // Equipment-enhanced stats
  protected effectiveMaxHealth: number;
  protected effectiveDefense: number;
  protected effectiveEnergyRegen: number;
  
  // Special effects
  private glowEffect: Phaser.GameObjects.Graphics | null = null;

  constructor(
    scene: Phaser.Scene, 
    x: number, 
    y: number, 
    maxHealth: number = 100, 
    defense: number = 0.01, 
    towerLimit: number = 4, 
    cooldown: number = 5000,
    energyRegen: number = 0
  ) {
    super(scene, x, y);
    
    this.baseMaxHealth = maxHealth;
    this.baseDefense = defense;
    this.towerLimit = towerLimit;
    this.cooldown = cooldown;
    this.baseEnergyRegen = energyRegen;
    this.abilityCooldown = cooldown;
    
    // Initialize default values (will be overridden by subclasses)
    this.damageResistance = defense;
    this.specialAbility = 'None';
    
    // Initialize audio manager
    this.audioManager = AudioManager.getInstance(scene);
    
    // Calculate initial effective stats
    this.recalculateStats();
    
    // Set health to max
    this.health = this.effectiveMaxHealth;
    this.maxHealth = this.effectiveMaxHealth;
    
    // Create basic visuals first
    this.createVisuals();
    
    // Initialize enhanced visual effects
    // this.initializeVisualEffects();
  }

  private initializeVisualEffects(): void {
    // Create atmospheric glow
    this.createAtmosphereGlow();
    
    // Create energy field
    this.createEnergyField();
    
    // Create orbital particles
    this.createOrbitalParticles();
    
    // Create protective shield visual
    this.createProtectiveShield();
    
    // Create core pulse effect
    this.createCorePulse();
  }

  private createAtmosphereGlow(): void {
    // Outer atmosphere
    const outerAtmosphere = this.scene.add.circle(this.x, this.y, 80, this.getAtmosphereColor(), 0.1);
    outerAtmosphere.setDepth(this.depth - 3);
    
    // Inner atmosphere
    const innerAtmosphere = this.scene.add.circle(this.x, this.y, 60, this.getAtmosphereColor(), 0.2);
    innerAtmosphere.setDepth(this.depth - 2);
    
    // Breathing animation
    this.scene.tweens.add({
      targets: outerAtmosphere,
      scaleX: 1.2,
      scaleY: 1.2,
      alpha: 0.05,
      duration: 3000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    this.scene.tweens.add({
      targets: innerAtmosphere,
      scaleX: 1.1,
      scaleY: 1.1,
      alpha: 0.3,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createEnergyField(): void {
    // Energy field rings
    for (let i = 0; i < 3; i++) {
      const ring = this.scene.add.graphics();
      ring.lineStyle(1, this.getEnergyColor(), 0.4 - (i * 0.1));
      ring.strokeCircle(this.x, this.y, 45 + (i * 10));
      ring.setDepth(this.depth - 1);
      
      // Rotating animation
      this.scene.tweens.add({
        targets: ring,
        rotation: Math.PI * 2,
        duration: 8000 + (i * 2000),
        repeat: -1,
        ease: 'Linear'
      });
      
      // Pulsing effect
      this.scene.tweens.add({
        targets: ring,
        alpha: 0.1,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 2500 + (i * 500),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
  }

  private createOrbitalParticles(): void {
    // Create orbital particle system
    const particleCount = 8;
    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount;
      const radius = 70;
      const particleX = this.x + Math.cos(angle) * radius;
      const particleY = this.y + Math.sin(angle) * radius;
      
      const particle = this.scene.add.circle(particleX, particleY, 2, this.getParticleColor(), 0.8);
      particle.setDepth(this.depth + 1);
      
      // Orbital motion
      this.scene.tweens.add({
        targets: particle,
        x: this.x + Math.cos(angle + Math.PI * 2) * radius,
        y: this.y + Math.sin(angle + Math.PI * 2) * radius,
        duration: 12000,
        repeat: -1,
        ease: 'Linear'
      });
      
      // Size pulsing
      this.scene.tweens.add({
        targets: particle,
        scaleX: 1.5,
        scaleY: 1.5,
        alpha: 0.4,
        duration: 1000 + (i * 200),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
  }

  private createProtectiveShield(): void {
    // Shield generator points
    const shieldPoints = 6;
    for (let i = 0; i < shieldPoints; i++) {
      const angle = (Math.PI * 2 * i) / shieldPoints;
      const pointX = this.x + Math.cos(angle) * 85;
      const pointY = this.y + Math.sin(angle) * 85;
      
      // Shield node
      const shieldNode = this.scene.add.graphics();
      shieldNode.fillStyle(this.getShieldColor(), 0.6);
      shieldNode.fillCircle(pointX, pointY, 3);
      shieldNode.lineStyle(1, 0xffffff, 0.8);
      shieldNode.strokeCircle(pointX, pointY, 3);
      shieldNode.setDepth(this.depth + 2);
      
      // Connect shield nodes
      if (i < shieldPoints - 1) {
        const nextAngle = (Math.PI * 2 * (i + 1)) / shieldPoints;
        const nextX = this.x + Math.cos(nextAngle) * 85;
        const nextY = this.y + Math.sin(nextAngle) * 85;
        
        const connection = this.scene.add.graphics();
        connection.lineStyle(1, this.getShieldColor(), 0.3);
        connection.lineBetween(pointX, pointY, nextX, nextY);
        connection.setDepth(this.depth + 1);
        
        // Pulsing connection
        this.scene.tweens.add({
          targets: connection,
          alpha: 0.1,
          duration: 2000 + (i * 300),
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut'
        });
      }
      
      // Node pulsing
      this.scene.tweens.add({
        targets: shieldNode,
        scaleX: 1.3,
        scaleY: 1.3,
        duration: 1500 + (i * 250),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
  }

  private createCorePulse(): void {
    // Core pulse effect
    const corePulse = this.scene.add.circle(this.x, this.y, 15, this.getCoreColor(), 0.8);
    corePulse.setDepth(this.depth + 3);
    
    // Core glow
    const coreGlow = this.scene.add.circle(this.x, this.y, 25, this.getCoreColor(), 0.4);
    coreGlow.setDepth(this.depth + 2);
    
    // Heartbeat-like pulsing
    this.scene.tweens.add({
      targets: [corePulse, coreGlow],
      scaleX: 1.3,
      scaleY: 1.3,
      alpha: 0.5,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Power2.easeInOut'
    });
    
    // Continuous energy emanation
    this.scene.time.addEvent({
      delay: 3000,
      callback: () => this.createEnergyEmanation(),
      loop: true
    });
  }

  private createEnergyEmanation(): void {
    if (!this.scene) return;
    // Create energy wave emanating from core
    const energyWave = this.scene.add.graphics();
    energyWave.lineStyle(2, this.getEnergyColor(), 0.6);
    energyWave.strokeCircle(this.x, this.y, 20);
    energyWave.setDepth(this.depth + 1);
    
    this.scene.tweens.add({
      targets: energyWave,
      scaleX: 4,
      scaleY: 4,
      alpha: 0,
      duration: 2000,
      ease: 'Power2.easeOut',
      onComplete: () => energyWave.destroy()
    });
  }

  // Recalculate stats based on base stats + equipment bonuses
  protected recalculateStats(): void {
    const equipmentStats = this.getEquipmentStats();
    
    this.effectiveMaxHealth = this.baseMaxHealth + (equipmentStats.earthHealth || 0);
    this.effectiveDefense = Math.min(0.9, this.baseDefense + (equipmentStats.earthDefense || 0)); // Cap at 90%
    this.effectiveEnergyRegen = this.baseEnergyRegen + (equipmentStats.energyRegen || 0);
    
    // Update actual health if it exceeds new max
    if (this.health > this.effectiveMaxHealth) {
      this.health = this.effectiveMaxHealth;
    }
    this.maxHealth = this.effectiveMaxHealth;
  }

  // Get total equipment stats from equipped items
  protected getEquipmentStats(): EquipmentStats {
    const equipmentManager = getEquipmentManager();
    return equipmentManager.calculateTotalStats();
  }

  override update(time: number, delta: number): void {
    // super.update(time, delta);
    
    // Recalculate stats periodically (in case equipment changed)
    if (time % 1000 < delta) { // Every ~1 second
      this.recalculateStats();
    }
    
    // Energy regeneration
    if (this.effectiveEnergyRegen > 0) {
      // Notify scene about energy regen (if scene has addEnergyCredits method)
      const energyToAdd = (this.effectiveEnergyRegen * delta) / 1000;
      if ((this.scene as any).addEnergyCredits) {
        (this.scene as any).addEnergyCredits(Math.floor(energyToAdd));
      }
    }
  }

  override takeDamage(amount: number): void {
    // Apply defense
    const actualDamage = amount * (1 - this.effectiveDefense);
    super.takeDamage(actualDamage);
    
    // Play damage sound
    this.audioManager.playSFX(SoundEffect.EARTH_DAMAGE);
    
    // Show damage number with defense calculation
    this.showDamageNumber(actualDamage, amount);
  }

  protected showDamageNumber(actualDamage: number, originalDamage: number): void {
    // Check if scene is available
    if (!this.scene || !this.scene.add) {
      // console.warn('Scene not available for damage number display');
      return;
    }

    const damageText = originalDamage !== actualDamage 
      ? `${actualDamage.toFixed(0)} (${(this.effectiveDefense * 100).toFixed(0)}% blocked)`
      : `${actualDamage.toFixed(0)}`;
    
    const text = this.scene.add.text(this.x + 30, this.y - 30, damageText, {
      fontSize: '14px',
      color: originalDamage !== actualDamage ? '#ffaa00' : '#ff0000',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    if (this.scene.tweens) {
      this.scene.tweens.add({
        targets: text,
        y: text.y - 30,
        alpha: 0,
        duration: 1000,
        ease: 'Power2',
        onComplete: () => {
          text.destroy();
        }
      });
    } else {
      // Fallback if tweens not available
      setTimeout(() => {
        text.destroy();
      }, 1000);
    }
  }

  protected createVisuals(): void {
    // Earth body (will be overridden by specific Earth types)
    const earth = this.scene.add.circle(0, 0, 50, 0x4444aa);
    this.add(earth);
  }

  // Special ability methods
  abstract useSpecialAbility(): void;
  
  // Method that subclasses expect to exist
  abstract activateAbility(): void;
  
  // Abstract upgrade method that subclasses implement
  abstract upgrade(): void;

  // Play ability effect (expected by subclasses)
  protected playAbilityEffect(): void {
    this.showAbilityEffect();
  }

  // Check if special ability can be used
  canUseSpecialAbility(): boolean {
    const currentTime = this.scene.time.now;
    return currentTime - this.lastAbilityTime >= this.cooldown;
  }

  // Trigger special ability
  triggerSpecialAbility(): boolean {
    if (!this.canUseSpecialAbility()) return false;
    
    this.lastAbilityTime = this.scene.time.now;
    
    // Play ability sound
    this.audioManager.playEarthAbility();
    
    this.useSpecialAbility();
    this.showAbilityEffect();
    return true;
  }

  // Visual effect when ability is used
  protected showAbilityEffect(): void {
    // Check if scene is available
    if (!this.scene || !this.scene.add) {
      console.warn('Scene not available for ability effect display');
      return;
    }

    // Create glow effect
    if (this.glowEffect) {
      this.glowEffect.destroy();
    }
    
    this.glowEffect = this.scene.add.graphics();
    this.glowEffect.lineStyle(6, 0x00ff00, 0.8);
    this.glowEffect.strokeCircle(this.x, this.y, 60);
    
    // Animate glow
    if (this.scene.tweens) {
      this.scene.tweens.add({
        targets: this.glowEffect,
        alpha: 0,
        scaleX: 1.5,
        scaleY: 1.5,
        duration: 1000,
        ease: 'Power2',
        onComplete: () => {
          if (this.glowEffect) {
            this.glowEffect.destroy();
            this.glowEffect = null;
          }
        }
      });
    } else {
      // Fallback if tweens not available
      setTimeout(() => {
        if (this.glowEffect) {
          this.glowEffect.destroy();
          this.glowEffect = null;
        }
      }, 1000);
    }
  }

  // Getters that GamePlayScene expects
  getTowerLimit(): number {
    return this.towerLimit;
  }

  override getMaxHealth(): number {
    return this.effectiveMaxHealth;
  }

  getDefense(): number {
    return this.effectiveDefense;
  }
  
  // Method expected by GamePlayScene
  getDamageResistance(): number {
    return this.damageResistance;
  }

  getEnergyRegen(): number {
    return this.effectiveEnergyRegen;
  }

  getCooldown(): number {
    return this.cooldown;
  }
  
  // Method expected by GamePlayScene
  getSpecialAbility(): string {
    return this.specialAbility;
  }
  
  // Method expected by GamePlayScene
  getLevel(): number {
    return this.level;
  }

  getAbilityCooldownRemaining(): number {
    if (!this.scene) return 0;
    const currentTime = this.scene.time.now;
    const elapsed = currentTime - this.lastAbilityTime;
    return Math.max(0, this.cooldown - elapsed);
  }

  // Get stats display for UI
  getStatsDisplay(): string {
    const cooldownRemaining = this.getAbilityCooldownRemaining();
    const cooldownText = cooldownRemaining > 0 
      ? ` | Cooldown: ${(cooldownRemaining / 1000).toFixed(1)}s`
      : ' | Ability: Ready';
    
    return `Health: ${this.health.toFixed(0)}/${this.effectiveMaxHealth.toFixed(0)} | ` +
           `Defense: ${(this.effectiveDefense * 100).toFixed(0)}% | ` +
           `Towers: ${this.towerLimit}` +
           (this.effectiveEnergyRegen > 0 ? ` | Energy: +${this.effectiveEnergyRegen.toFixed(1)}/s` : '') +
           cooldownText;
  }

  // Check if health ratio is different from display (for UI updates)
  getHealthRatio(): number {
    return this.health / this.effectiveMaxHealth;
  }

  // Heal Earth (useful for some abilities or equipment)
  override heal(amount: number): void {
    const oldHealth = this.health;
    this.health = Math.min(this.effectiveMaxHealth, this.health + amount);
    const actualHeal = this.health - oldHealth;
    
    if (actualHeal > 0) {
      // Play heal sound
      this.audioManager.playSFX(SoundEffect.EARTH_HEAL);
      
      this.showHealNumber(actualHeal);
    }
  }

  protected showHealNumber(healAmount: number): void {
    // Check if scene is available
    if (!this.scene || !this.scene.add) {
      // console.warn('Scene not available for heal number display');
      return;
    }

    const healText = this.scene.add.text(this.x - 30, this.y - 30, `+${healAmount.toFixed(0)}`, {
      fontSize: '14px',
      color: '#00ff00',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    if (this.scene.tweens) {
      this.scene.tweens.add({
        targets: healText,
        y: healText.y - 30,
        alpha: 0,
        duration: 1000,
        ease: 'Power2',
        onComplete: () => {
          healText.destroy();
        }
      });
    } else {
      // Fallback if tweens not available
      setTimeout(() => {
        healText.destroy();
      }, 1000);
    }
  }

  // Abstract methods for different Earth types to override
  protected abstract getAtmosphereColor(): number;
  protected abstract getEnergyColor(): number;
  protected abstract getParticleColor(): number;
  protected abstract getShieldColor(): number;
  protected abstract getCoreColor(): number;
} 